import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    CheckCircle2,
    Clock,
    Pencil,
    Trash2,
    Undo2,
    ChevronRight,
    LayoutTemplate,
    Info,
    Share2,
    History
} from "lucide-react";
import { toast } from "react-toastify";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Loader from "../UI/Loader/Loader";
import ConfirmDialog from "../UI/Modal/ConfirmDialog";
import { useUserContext } from "../../contexts/UserContext";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import {
    deleteExperienceById,
    getExperienceById,
    RevertExperienceVersion
} from "../../utils/APIs";

const ExperienceDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [experience, setExperience] = useState(null);
    const [offerMappings, setOfferMappings] = useState({});
    const [loading, setLoading] = useState(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [error, setError] = useState("");
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
    const [deleteState, setDeleteState] = useState({
        version: null,
        loading: false,
        isLastDraft: false,
        modalMessage: ""
    });

    const extractUserName = (val) => {
        if (!val) return "N/A";
        if (typeof val === 'string') return val;
        if (typeof val === 'object') return val.name || val.email || val.username || "N/A";
        return String(val);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return "N/A";
        const optionsDate = {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        };
        const formattedDate = date.toLocaleDateString('en-US', optionsDate);
        const hours12 = date.getHours() % 12 || 12;
        const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
        const formattedTime = hours12 + ':' + ('0' + date.getMinutes()).slice(-2) + ' ' + ampm;
        return `${formattedDate}, ${formattedTime}`;
    };

    const fetchExperienceDetails = async () => {
        try {
            setLoading(true);
            const experienceData = await getExperienceById(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                id,
                userData?.defaultAdobeIO ? userData?.defaultAdobeIO?._id : null,
                userData?.defaultContentful ? userData?.defaultContentful._id : null,
                userData?.defaultTealiumKey ? userData?.defaultTealiumKey?._id : null,
                userData?.defaultGithub ? userData?.defaultGithub?._id : null
            );

            const adobeOffers = experienceData.data?.associated_adobe_target_offer_id || [];
            const contentfulOffers = experienceData.data?.associated_contentful_offer_id || [];
            const tealiumOffers = experienceData.data?.associated_tealium_offer_id || [];
            const githubActivations = experienceData.data?.associated_github_entry_path || [];

            const temp = {};
            adobeOffers.forEach((x) => { temp[x] = { destination: "Adobe Target" }; });
            contentfulOffers.forEach((x) => { temp[x] = { destination: "Contentful" }; });
            tealiumOffers.forEach((x) => { temp[x] = { destination: "Tealium" }; });
            githubActivations.forEach((x) => { temp[x] = { destination: "Github" }; });

            setOfferMappings(temp);
            setExperience(experienceData.data);
            setLoading(false);
        } catch (err) {
            setError(err?.message || "Failed to load experience details");
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchExperienceDetails();
        }
    }, [id, userData]);

    const handleBack = () => {
        navigate("/experience/manage-experiences");
    };

    const handleEdit = () => {
        navigate(`/experience/edit-experience/${experience?._id}`);
    };

    const handleRevert = async () => {
        setLoading(true);
        try {
            const result = await RevertExperienceVersion(userData?.token, userData?.selectedOrganization?.org_token, experience._id);
            if (result.status) {
                toast.success(result.message);
                fetchExperienceDetails();
            }
        } catch (err) {
            toast.error(err?.message || err?.response?.message || "Failed to revert version");
            console.error('Revert error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteVersionConfirm = async () => {
        if (!deleteState.version) return;

        try {
            setDeleteState((prev) => ({ ...prev, loading: true }));

            if (deleteState.isLastDraft) {
                const res = await deleteExperienceById(userData?.token, userData?.selectedOrganization?.org_token, {
                    id: experience._id
                });
                if (res.status) {
                    toast.success(res?.message || "Experience deleted successfully.");
                    navigate("/experience/manage-experiences");
                    return;
                }
            }

            const result = await deleteExperienceById(userData?.token, userData?.selectedOrganization?.org_token, {
                id: experience._id,
                version: deleteState.version,
            });

            if (result.status) {
                setExperience((prev) => ({
                    ...prev,
                    versions: prev.versions.filter((v) => v.version_no !== deleteState.version),
                }));
            }

            toast.success("Version deleted successfully.");
            setDeleteState({ version: null, loading: false, isLastDraft: false, modalMessage: "" });
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to delete version.");
            setDeleteState({ version: null, loading: false, isLastDraft: false, modalMessage: "" });
        }
    };

    const handleDeleteExp = async () => {
        setLoading(true);
        try {
            const result = await deleteExperienceById(userData?.token, userData?.selectedOrganization?.org_token, {
                id: experience._id,
            });
            if (result.status) {
                setLoading(false);
                handleBack();
            }
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const handleDeleteVersionClick = (ver) => {
        const isPublishedVersion = ver.version_no === experience?.published_version;

        if (isPublishedVersion) {
            toast.error("Published version cannot be deleted.");
            return;
        }

        const draftVersions = (experience?.versions || []).filter(
            (v) => v.version_no !== experience?.published_version
        );

        const isLastDraft = !experience?.published_version && draftVersions.length <= 1;

        const modalMessage = isLastDraft
            ? content.EXPERIENCE_DETAILS_CONTENT.delete_last_draft_version_message
            : `${content.EXPERIENCE_DETAILS_CONTENT.delete_version_message} ${ver.version_no}?`;

        setDeleteState({
            version: ver.version_no,
            loading: false,
            isLastDraft,
            modalMessage
        });
    };

    const allOffers = useMemo(() => {
        if (!experience) return [];
        return [
            ...(experience.associated_adobe_target_offer_id || []).map((offerId) => ({
                id: offerId,
                label: content.EXPERIENCE_DETAILS_CONTENT.offer_id,
            })),
            ...(experience.associated_contentful_offer_id || []).map((offerId) => ({
                id: offerId,
                label: content.EXPERIENCE_DETAILS_CONTENT.contentful_entry_id,
            })),
            ...(experience.associated_tealium_offer_id || []).map((offerId) => ({
                id: offerId,
                label: content.EXPERIENCE_DETAILS_CONTENT.tealium_offer_id,
            })),
            ...(experience.associated_github_entry_path || []).map((offerId) => ({
                id: offerId,
                label: content.EXPERIENCE_DETAILS_CONTENT.github_entry_path,
            })),
        ];
    }, [experience, content]);

    return (
        <>
            {loading && <Loader />}
            <main id="main" className="stride-page-shell bg-gradient-to-b from-surface via-white to-white min-h-screen space-y-6">
                {/* Back navigation */}
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBack}
                        icon={<ArrowLeft size={16} />}
                    >
                        {content.EXPERIENCE_DETAILS_CONTENT.back_cta_text}
                    </Button>
                </div>

                {error && (
                    <div className="p-4 rounded-xl bg-danger-soft text-danger text-sm font-medium border border-danger/20 flex items-center justify-between">
                        <span>{error}</span>
                        <Button variant="ghost" size="xs" onClick={() => setError("")}>Dismiss</Button>
                    </div>
                )}

                {/* Header & Primary Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <p className="stride-eyebrow">Experience Details</p>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl md:text-3xl stride-heading m-0 line-clamp-1">
                                {experience?.name || "Experience"}
                            </h1>
                            {experience?.published_version ? (
                                <span className="stride-badge-pill bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                                    <CheckCircle2 size={12} className="stroke-[2.5]" />
                                    Published (v{experience.published_version})
                                </span>
                            ) : (
                                <span className="stride-badge-pill bg-amber-50 text-amber-700 border border-amber-200/60">
                                    <Clock size={12} className="stroke-[2.5]" />
                                    Draft
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                        {experience?.published_version && (
                            <Button
                                id="experience-detail-edit-btn-id"
                                variant="primary"
                                size="sm"
                                onClick={handleEdit}
                                icon={<Pencil size={14} />}
                            >
                                {content.EXPERIENCE_DETAILS_CONTENT.edit_cta_text}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteModalOpen(true)}
                            icon={<Trash2 size={14} className="text-danger" />}
                            className="text-danger hover:bg-danger-soft hover:border-danger/30"
                        >
                            {content.EXPERIENCE_DETAILS_CONTENT.delete_experience_cta || "Delete"}
                        </Button>
                    </div>
                </div>

                {/* Metadata Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* General Details Card */}
                    <Card>
                        <Card.Header
                            title="General Information"
                            icon={<LayoutTemplate size={16} className="text-primary" />}
                        />
                        <Card.Content className="space-y-4 pt-2">
                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                    {content.EXPERIENCE_DETAILS_CONTENT.template_name}
                                </span>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {experience?.templates?.length > 0 ? (
                                        experience.templates.map((tpl) => (
                                            <span key={tpl._id || tpl.name} className="stride-badge-pill bg-surface-alt text-stride-body border border-border">
                                                {tpl.name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-sm font-medium text-stride-body">N/A</span>
                                    )}
                                </div>
                            </div>

                            <div className="h-px bg-border-subtle" />

                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                    {content.EXPERIENCE_DETAILS_CONTENT.last_edited_by}
                                </span>
                                <span className="text-sm font-semibold text-stride-heading">
                                    {extractUserName(experience?.managed_by)}
                                </span>
                            </div>

                            <div className="h-px bg-border-subtle" />

                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                    {content.EXPERIENCE_DETAILS_CONTENT.last_edited_at}
                                </span>
                                <span className="text-sm text-stride-muted tabular-nums font-medium">
                                    {experience?.updatedAt ? formatDate(experience.updatedAt) : "N/A"}
                                </span>
                            </div>
                        </Card.Content>
                    </Card>

                    {/* Creation & Origin Card */}
                    <Card>
                        <Card.Header
                            title="Origin & Governance"
                            icon={<Info size={16} className="text-primary" />}
                        />
                        <Card.Content className="space-y-4 pt-2">
                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                    {content.EXPERIENCE_DETAILS_CONTENT.source}
                                </span>
                                <span className="text-sm font-semibold text-stride-heading capitalize">
                                    {experience?.source || "N/A"}
                                </span>
                            </div>

                            <div className="h-px bg-border-subtle" />

                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                    {content.EXPERIENCE_DETAILS_CONTENT.created_by}
                                </span>
                                <span className="text-sm font-semibold text-stride-heading">
                                    {extractUserName(experience?.created_by)}
                                </span>
                            </div>

                            <div className="h-px bg-border-subtle" />

                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                    {content.EXPERIENCE_DETAILS_CONTENT.created_at}
                                </span>
                                <span className="text-sm text-stride-muted tabular-nums font-medium">
                                    {experience?.createdAt ? formatDate(experience.createdAt) : "N/A"}
                                </span>
                            </div>
                        </Card.Content>
                    </Card>
                </div>

                {/* Available Offers Card */}
                <Card>
                    <Card.Header
                        title={content.EXPERIENCE_DETAILS_CONTENT.available_offers}
                        subtitle="External activation targets and content IDs associated with this experience"
                        icon={<Share2 size={16} className="text-primary" />}
                    />
                    <Card.Content className="pt-2">
                        {allOffers.length > 0 ? (
                            <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                                {allOffers.map((item, idx) => (
                                    <div key={idx} className="p-3.5 bg-white hover:bg-surface-subtle transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                        <div className="flex items-center gap-2.5">
                                            <span className="text-xs font-medium text-stride-muted">{item.label}:</span>
                                            <code className="text-xs font-mono font-semibold text-stride-heading bg-surface-alt px-2 py-0.5 rounded border border-border">
                                                {item.id}
                                            </code>
                                        </div>
                                        <span className="stride-badge-pill bg-primary-soft text-primary border border-primary/20 self-start sm:self-auto">
                                            {offerMappings[item.id]?.destination || "Destination"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-stride-muted italic py-2">
                                {content.EXPERIENCE_DETAILS_CONTENT.no_offers_available}
                            </p>
                        )}
                    </Card.Content>
                </Card>

                {/* Version History Card */}
                {experience?.versions?.length > 0 && (
                    <Card>
                        <Card.Header
                            title={content.EXPERIENCE_DETAILS_CONTENT.versions_heading}
                            subtitle="Audit log of all drafts and published snapshots of this experience"
                            icon={<History size={16} className="text-primary" />}
                        />
                        <Card.Content className="space-y-3 pt-2">
                            {experience.versions.map((ver, index) => {
                                const isPublished = ver.version_no === experience.published_version;
                                const isLatestDraft = !experience.published_version &&
                                    ver.version_no === experience?.versions?.[experience.versions.length - 1]?.version_no;

                                return (
                                    <details
                                        key={index}
                                        className="group bg-surface-subtle border border-border rounded-xl overflow-hidden transition-all"
                                    >
                                        <summary className="cursor-pointer px-4 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-surface transition-colors select-none">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <ChevronRight size={16} className="text-stride-muted transition-transform group-open:rotate-90 flex-shrink-0" />
                                                <span className="font-bold text-sm text-stride-heading">
                                                    {content.EXPERIENCE_DETAILS_CONTENT.version_label} {ver.version_no}
                                                </span>
                                                <span className={`stride-badge-pill ${
                                                    isPublished
                                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
                                                        : 'bg-amber-50 text-amber-700 border border-amber-200/60'
                                                }`}>
                                                    {isPublished ? (
                                                        <>
                                                            <CheckCircle2 size={11} className="stroke-[2.5]" />
                                                            {content.EXPERIENCE_DETAILS_CONTENT.status_published}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Clock size={11} className="stroke-[2.5]" />
                                                            {content.EXPERIENCE_DETAILS_CONTENT.status_draft}
                                                        </>
                                                    )}
                                                </span>
                                                {ver.version_source && (
                                                    <span className="stride-badge-pill bg-surface-alt text-stride-muted border border-border">
                                                        {ver.version_source}
                                                    </span>
                                                )}
                                                {ver.published_at && (
                                                    <span className="text-xs text-stride-muted tabular-nums">
                                                        Published: {formatDate(ver.published_at)}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-1.5 self-end sm:self-auto" onClick={(e) => e.stopPropagation()}>
                                                {/* Edit Version button */}
                                                <button
                                                    type="button"
                                                    id={isLatestDraft ? "experience-detail-edit-btn-id" : undefined}
                                                    title="Edit Version"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(`/experience/edit-experience/${experience._id}`, {
                                                            state: { version: ver.version_no },
                                                        });
                                                    }}
                                                    className="p-1.5 rounded-lg text-stride-muted hover:text-primary hover:bg-white border border-transparent hover:border-border transition-colors cursor-pointer bg-transparent"
                                                >
                                                    <Pencil size={15} />
                                                </button>

                                                {/* Revert Version button */}
                                                {experience?.backup_published_version && isPublished && (
                                                    <button
                                                        type="button"
                                                        title="Revert Last Published Version"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setRevertConfirmOpen(true);
                                                        }}
                                                        className="p-1.5 rounded-lg text-stride-muted hover:text-primary hover:bg-white border border-transparent hover:border-border transition-colors cursor-pointer bg-transparent"
                                                    >
                                                        <Undo2 size={15} />
                                                    </button>
                                                )}

                                                {/* Delete Version button */}
                                                {!isPublished && (
                                                    <button
                                                        type="button"
                                                        title="Delete Version"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleDeleteVersionClick(ver);
                                                        }}
                                                        className="p-1.5 rounded-lg text-stride-muted hover:text-danger hover:bg-danger-soft border border-transparent hover:border-danger/20 transition-colors cursor-pointer bg-transparent"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </summary>

                                        <div className="px-5 pb-4 pt-2 border-t border-border-subtle bg-white text-xs text-stride-body space-y-1.5">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-stride-muted">{content.EXPERIENCE_DETAILS_CONTENT.version_created_by}:</span>
                                                <span>{extractUserName(ver.created_by)}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-stride-muted">{content.EXPERIENCE_DETAILS_CONTENT.version_created_at}:</span>
                                                <span className="tabular-nums">{ver.created_at ? formatDate(ver.created_at) : "N/A"}</span>
                                            </div>
                                            {ver.updated_at && (
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-stride-muted">{content.EXPERIENCE_DETAILS_CONTENT.version_updated_at}:</span>
                                                    <span className="tabular-nums">{formatDate(ver.updated_at)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </details>
                                );
                            })}
                        </Card.Content>
                    </Card>
                )}
            </main>

            {/* Delete Experience Modal */}
            <ConfirmDialog
                isOpen={deleteModalOpen}
                title={content.EXPERIENCE_DETAILS_CONTENT.delete_experience_title}
                description={content.EXPERIENCE_DETAILS_CONTENT.delete_experience_message}
                confirmText={content.EXPERIENCE_DETAILS_CONTENT.delete_experience_cta || "Delete"}
                variant="danger"
                loading={loading}
                onConfirm={handleDeleteExp}
                onClose={() => setDeleteModalOpen(false)}
            />

            {/* Delete Version Modal */}
            {deleteState.version && (
                <ConfirmDialog
                    isOpen={Boolean(deleteState.version)}
                    title={content.EXPERIENCE_DETAILS_CONTENT.delete_version_title}
                    description={deleteState?.modalMessage}
                    confirmText={content.EXPERIENCE_DETAILS_CONTENT.delete_version_cta || "Delete"}
                    variant="danger"
                    loading={deleteState.loading}
                    onConfirm={handleDeleteVersionConfirm}
                    onClose={() => setDeleteState({ version: null, loading: false, isLastDraft: false, modalMessage: "" })}
                />
            )}

            {/* Revert Version Modal */}
            {revertConfirmOpen && (
                <ConfirmDialog
                    isOpen={revertConfirmOpen}
                    title={content.EXPERIENCE_DETAILS_CONTENT?.revert_version_title}
                    description={content.EXPERIENCE_DETAILS_CONTENT?.revert_version_message}
                    confirmText={content.EXPERIENCE_DETAILS_CONTENT?.revert_version_cta || "Revert"}
                    variant="warning"
                    loading={loading}
                    onConfirm={async () => {
                        setRevertConfirmOpen(false);
                        await handleRevert();
                    }}
                    onClose={() => setRevertConfirmOpen(false)}
                />
            )}
        </>
    );
};

export default ExperienceDetails;
